import { createElement, type ReactNode, type Ref } from 'react';
import classNames from 'classnames';

import styles from './Card.module.scss';

/**
 * `surface` is the portal's default card. `outlined` drops the fill for a card that sits on
 * another card. `tinted` is the pale, bordered tile that headline metrics sit in. `subtle`
 * is the grey of a read-only box inside a card, such as a step's context.
 */
export type CardVariant = 'surface' | 'outlined' | 'tinted' | 'subtle';

/** Inner padding: `xs` 0.75rem, `sm` 1rem, `md` 1.5rem, `lg` 2rem, `xl` 2.5rem. */
export type CardPadding = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** `md` is the page card's 16px corner; `sm` the 8px of an item card inside one. */
export type CardRadius = 'md' | 'sm';

/**
 * A coloured edge in `--card-accent`, which the caller sets: `border` colours the whole
 * border, as a task's priority does; `stripe` draws a 3px bar down the start edge, as a pen's
 * state does.
 */
export type CardAccent = 'border' | 'stripe';

/** How a card looks, apart from what element it is. */
export interface CardSurfaceStyle {
    variant?: CardVariant;
    padding?: CardPadding;
    radius?: CardRadius;
    accent?: CardAccent;
    /** Fills its parent's width. Off for cards that size to their grid or flex track. */
    fullWidth?: boolean;
    /** Hover and focus for a card that is a control: a choice, a row to pick. */
    interactive?: boolean;
    /** The chosen card among selectable ones. */
    selected?: boolean;
}

/** What `cardSurface` spreads onto an element: its class and the data attributes Card styles. */
export interface CardSurfaceAttributes {
    className: string;
    'data-variant': CardVariant;
    'data-padding': CardPadding;
    'data-radius': CardRadius;
    'data-accent': CardAccent | undefined;
    'data-full-width': boolean;
    'data-interactive': true | undefined;
    'data-selected': boolean | undefined;
}

/**
 * The attributes that give any element Card's look, for a card that has to be a `button` or
 * a `label`: spread them onto the element and add its own class with `className`.
 */
export function cardSurface(
    style: CardSurfaceStyle = {},
    className?: string
): CardSurfaceAttributes {
    const {
        variant = 'surface',
        padding = 'sm',
        radius = 'md',
        accent,
        fullWidth = true,
        interactive = false,
        selected = false,
    } = style;
    return {
        className: classNames(styles.card, className),
        'data-variant': variant,
        'data-padding': padding,
        'data-radius': radius,
        'data-accent': accent,
        'data-full-width': fullWidth,
        'data-interactive': interactive || undefined,
        'data-selected': interactive ? selected : undefined,
    };
}

export interface CardProps {
    variant?: CardVariant;
    padding?: CardPadding;
    radius?: CardRadius;
    accent?: CardAccent;
    /** Stretch to the parent's width. Off for cards that size to their grid or flex track. */
    fullWidth?: boolean;
    /** `section` for a card that is a landmark of its own, such as a titled panel. */
    as?: 'div' | 'section' | 'article' | 'li';
    /** Rendered above the body; usually a `CardHeader`. */
    header?: ReactNode;
    /** Rendered below the body; usually a `CardFooter`. */
    footer?: ReactNode;
    children?: ReactNode;
    className?: string;
    id?: string;
    /** For a card whose content updates while the reader works, such as running totals. */
    'aria-live'?: 'polite' | 'off';
    containerRef?: Ref<HTMLElement>;
}

/**
 * The one container for card-shaped content. It owns the surface, border, radius and
 * padding; what goes inside is laid out by the caller, so the body is not wrapped.
 */
export default function Card(props: CardProps) {
    const {
        variant,
        padding,
        radius,
        accent,
        fullWidth,
        as = 'div',
        header,
        footer,
        children,
        className,
        id,
        containerRef,
    } = props;

    return createElement(
        as,
        {
            ref: containerRef,
            id,
            'aria-live': props['aria-live'],
            ...cardSurface({ variant, padding, radius, accent, fullWidth }, className),
        },
        header,
        children,
        footer
    );
}

interface CardHeaderProps {
    title?: ReactNode;
    /**
     * Renders the title as this heading. Pick the level the page outline needs: the first
     * heading under the page's h1 is an h2.
     */
    level?: 'h2' | 'h3' | 'h4';
    /**
     * `card` is the card title's 20px, `section` the 24px of a block that names a section of
     * the page. Defaults to `section` for an h2 and `card` otherwise, so an h2 card title
     * that sits among h3 and h4 cards sets `size="card"` to look like them.
     */
    size?: 'card' | 'section';
    subtitle?: ReactNode;
    /** Sits left of the title, e.g. a module icon. */
    icon?: ReactNode;
    /** Right-aligned controls: a link, a badge, a toggle. */
    actions?: ReactNode;
    className?: string;
    /** Makes the title a focus target (tabIndex -1), for moving focus to the card. */
    titleRef?: Ref<HTMLElement>;
}

/** Title, optional subtitle and icon on the left, actions on the right. */
export function CardHeader(props: CardHeaderProps) {
    const { title, level, size, subtitle, icon, actions, className, titleRef } = props;
    const hasText = title != null || subtitle != null;
    const TitleTag = level ?? 'div';
    const titleSize = size ?? (level === 'h2' ? 'section' : 'card');

    return (
        <div className={classNames(styles.header, className)}>
            <div className={styles.headerContent}>
                {icon != null && <div className={styles.headerIcon}>{icon}</div>}
                {hasText && (
                    <div className={styles.headerText}>
                        {title != null &&
                            // createElement, as Card does: the tag varies, so JSX would type the
                            // ref per tag and reject one ref for all of them.
                            createElement(
                                TitleTag,
                                {
                                    ref: titleRef,
                                    tabIndex: titleRef ? -1 : undefined,
                                    className: styles.title,
                                    'data-size': titleSize,
                                },
                                title
                            )}
                        {subtitle != null && <div className={styles.subtitle}>{subtitle}</div>}
                    </div>
                )}
            </div>
            {actions != null && <div>{actions}</div>}
        </div>
    );
}

interface CardFooterProps {
    children?: ReactNode;
    className?: string;
}

/** Secondary text or actions under the body. */
export function CardFooter(props: CardFooterProps) {
    const { children, className } = props;
    return <div className={classNames(styles.footer, className)}>{children}</div>;
}
